// src/shared/email-search.js — how a store SEARCHES its mail (v129.106).
//
// Dubai Hills, 10 Aug: "Search bar in inbox and sent items. Save time rather
// than scrolling back all the emails. Though sorting is available now, it will
// still be easier if there is search bar just like in chat."
//
// Same store that asked for sorting on 4 Aug (v129.44, src/shared/email-sort.js)
// — sorting helped and they want the next step. Pure and zero-dependency for the
// same reason as its sibling: the rule is testable without a browser, and the
// list is already in memory so a search costs no API call and no new endpoint.
//
// ── THE TRAP THIS MODULE IS SHAPED AROUND ──────────────────────────────────
//
// A search only searches what is LOADED, and that is far more dangerous than a
// sort that only sorts what is loaded. A sort showing the A-Z of 50 emails still
// shows 50 emails; a search that finds NOTHING looks exactly like "that email
// does not exist" when the truth is "it is in a page nobody has fetched". A
// store could conclude a customer never wrote to them.
//
// So `searchNote()` is not decoration — whenever a query is active and older
// pages remain, the UI must say what was actually searched and point at "Load
// older emails". Zero matches gets the loudest version of that message.
//
// (The honest long-term answer is a server-side search on the inbox endpoint.
// Client-side matches how chat search already works, needs no new API, and is
// what the store asked for — "just like in chat". Revisit if a mailbox grows
// past what Load-older can reasonably walk.)

// Terms are AND-ed: "alex invoice" finds the mail that mentions BOTH, which is
// how people actually narrow a search. Capped so a pasted paragraph cannot turn
// one keystroke into hundreds of substring scans per row.
export const MAX_TERMS = 8;

export function searchTerms(q) {
  return String(q == null ? '' : q)
    .toLowerCase()
    .split(/\s+/)
    .filter(function (t) { return !!t; })
    .slice(0, MAX_TERMS);
}

// What a row is searched BY is exactly what the row DISPLAYS: the subject, the
// from/to line, and the preview snippet. Searching a field the reader cannot see
// produces matches they cannot explain — the same class of confusion as sorting
// by an invisible column (see email-sort.js whoValue).
//
// NOT searched: the full body. The list API returns only a preview, so a body
// search would match on the first rows and miss on the rest depending on which
// ones happened to be opened. Inconsistent matching is worse than no matching.
export function emailHaystack(m, folder) {
  if (!m) return '';
  var out = String(m.subject || '');
  var to = Array.isArray(m.to) ? m.to.join(', ') : String(m.to || '');
  if (folder === 'out' || m.direction === 'out') {
    out += ' ' + to;
  } else {
    out += ' ' + String(m.from || '');
  }
  if (m.preview) out += ' ' + String(m.preview);
  return out.toLowerCase();
}

// Never mutates, and an empty/blank query returns the list UNTOUCHED (not a
// copy-with-everything) so the no-search path stays byte-identical to what the
// inbox did before search existed.
export function filterEmails(list, q, folder) {
  var rows = Array.isArray(list) ? list : [];
  var terms = searchTerms(q);
  if (!terms.length) return rows;
  return rows.filter(function (m) {
    var hay = emailHaystack(m, folder);
    for (var i = 0; i < terms.length; i++) {
      if (hay.indexOf(terms[i]) === -1) return false;
    }
    return true;
  });
}

// The caveat line, or null when there is nothing to caveat.
//   q       — the raw query text
//   hasMore — the inbox's own "a full page came back, so older mail exists" flag
//   matches — how many rows the query matched (drives the loud zero-match wording)
export function searchNote(q, hasMore, matches) {
  if (!searchTerms(q).length) return null;
  if (!hasMore) return null;                 // the whole mailbox is loaded — the answer is complete
  if (!matches) {
    return 'No matches in the emails loaded so far — older emails have not been '
      + 'searched yet. Tap "Load older emails" at the bottom, then search again.';
  }
  return 'Searching the emails loaded so far — tap "Load older emails" at the bottom to search the rest.';
}
